import { api } from '../api/client';
import type { PublicUser } from '../types';
import { useAuthStore } from './store';

export interface SignInResponse {
  token: string;
  user: PublicUser;
}

export function signInWithGoogle(idToken: string): Promise<SignInResponse> {
  return api.post<SignInResponse>('/auth/google', { idToken }, { anonymous: true });
}

/** Sliding-session renewal: trades the current (still-valid) token for a
 *  fresh one with a full window. A 401 here is handled by the api client,
 *  which clears the session and bounces to /sign-in. */
export function refreshSession(): Promise<SignInResponse> {
  return api.post<SignInResponse>('/auth/refresh', {});
}

export function fetchMe(): Promise<PublicUser> {
  return api.get<PublicUser>('/me');
}

// ─── Email + password ─────────────────────────────────────────────

export function emailVerify(
  email: string,
  code: string,
  password: string,
): Promise<SignInResponse> {
  return api.post<SignInResponse>(
    '/auth/email/verify',
    { email, code, password },
    { anonymous: true },
  );
}

export function emailResendCode(email: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/auth/email/resend-code', { email }, { anonymous: true });
}

export function emailLogin(email: string, password: string): Promise<SignInResponse> {
  return api.post<SignInResponse>('/auth/email/login', { email, password }, { anonymous: true });
}

export function emailForgot(email: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/auth/email/forgot', { email }, { anonymous: true });
}

export function emailReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(
    '/auth/email/reset',
    { email, code, newPassword },
    { anonymous: true },
  );
}

/**
 * Rotates the password. The API revokes every session minted before —
 * including the one making this call — and returns a fresh token, which is
 * stored here so this tab stays signed in while every other device is
 * signed out.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const resp = await api.post<{ ok: boolean; token: string }>('/auth/email/change-password', {
    currentPassword,
    newPassword,
  });
  const { user, setSession } = useAuthStore.getState();
  if (user) setSession(resp.token, user);
}

/** Permanently deletes the caller's account. The client must clear the session. */
export function deleteAccount(): Promise<{ ok: boolean }> {
  return api.delete<{ ok: boolean }>('/me');
}
